<template>
  <div class="document-verification-page">
    <div class="container">
      <!-- Mostra solo loader durante l'attesa della conferma del match (dopo successo Stripe) -->
      <template v-if="loading && !verifiedAlready && !pendingIdentity">
        <div class="verification-loader">
          <div class="spinner"></div>
          <p style="margin-top: 1.5em; color: white;">Verifica in corso...</p>
          <p style="margin-top: 0.5em; color: rgba(255,255,255,0.7); font-size: 0.9em;">Stiamo verificando i tuoi documenti</p>
        </div>
      </template>
      
      <!-- Contenuto normale quando non c'è loading attivo -->
      <template v-else>
        <img
          src="@/assets/img/verification.webp"
          alt="Logo"
          class="logo"
        />

        <template v-if="verifiedAlready">
          <h1>La tua identità è stata verificata</h1>
          <p>Non c'è nulla da fare qui.</p>
        </template>
        <template v-else-if="pendingIdentity">
          <h1>Documenti in fase di verifica</h1>
          <p>I tuoi documenti sono stati caricati e sono in attesa di validazione da parte dello staff.</p>
          <ul class="doc-status">
            <li v-if="!isMinor">Fronte: <strong>{{ uploadedIdentityFront ? (validIdentityFront ? 'Verificato' : 'Caricato') : 'Mancante' }}</strong></li>
            <li v-if="!isMinor">Retro: <strong>{{ uploadedIdentityBack ? (validIdentityBack ? 'Verificato' : 'Caricato') : 'Mancante' }}</strong></li>
            <li v-if="isMinor">Documento Tutore: <strong>{{ uploadedLegalGuardianIdentity ? (validLegalGuardianIdentity ? 'Verificato' : 'Caricato') : 'Mancante' }}</strong></li>
          </ul>
          <div class="hint" v-if="!manualRequired && (!uploadedIdentityFront || !uploadedIdentityBack) && !isMinor">
            Hai caricato solo una parte del documento. Completa fronte e retro per velocizzare la verifica.
          </div>
          <div class="hint" v-if="manualRequired && (!uploadedIdentityFront || !uploadedIdentityBack) && !isMinor">
            Puoi completare l'upload delle parti mancanti qui sotto.
          </div>
          <div v-if="manualRequired && (!uploadedIdentityFront || !uploadedIdentityBack)" class="manual-upload-box">
            <div class="upload-group" v-if="!uploadedIdentityFront">
              <label>Documento (fronte)</label>
              <input type="file" accept="image/*,.pdf" @change="e => frontFile = e.target.files[0]" />
            </div>
            <div class="upload-group" v-if="!uploadedIdentityBack && !isMinor">
              <label>Documento (retro)</label>
              <input type="file" accept="image/*,.pdf" @change="e => backFile = e.target.files[0]" />
            </div>
            <button class="btn-custom" :disabled="uploading || (!frontFile && !backFile)" @click="uploadMissing">
              <span v-if="!uploading">Carica parti mancanti</span>
              <span v-else>Caricamento...</span>
            </button>
          </div>
        </template>
        <template v-else>
          <!-- Mostra contenuto iniziale solo se non c'è loading e non c'è mismatch -->
          <template v-if="!loading && !dataMismatch">
            <h1 v-if="!manualRequired">Dobbiamo verificare la tua identità</h1>
            <h1 v-else>Carica manualmente i tuoi documenti</h1>
            <p v-if="!manualRequired">Per procedere con la verifica, assicurati di avere a disposizione il tuo documento d'identità.</p>
            <p v-else>
              Hai raggiunto il numero massimo di tentativi automatici ({{ attempts }}/{{ maxAttempts }}).<br />
              Carica fronte e retro del documento del {{ legalGuardian ? 'tutore' : 'utente' }} per la verifica manuale.
            </p>

            <div v-if="!manualRequired">
              <div class="attempts-info" v-if="attempts > 0 && attempts < maxAttempts">
                Tentativi falliti: {{ attempts }} / {{ maxAttempts }}
              </div>
              <button class="btn-custom" @click="startVerification" :disabled="loading">
                Inizia la verifica
              </button>
            </div>

            <div v-else class="manual-upload-box">
              <div class="upload-group">
                <label>Documento (fronte)</label>
                <input type="file" accept="image/*,.pdf" @change="e => frontFile = e.target.files[0]" />
              </div>
              <div class="upload-group">
                <label>Documento (retro)</label>
                <input type="file" accept="image/*,.pdf" @change="e => backFile = e.target.files[0]" />
              </div>
              <button class="btn-custom" :disabled="uploading || !frontFile || !backFile" @click="uploadManual">
                <span v-if="!uploading">Carica documenti</span>
                <span v-else>Caricamento...</span>
              </button>
              <div v-if="manualSuccess" class="success" style="margin-top:1em;">Documenti caricati, in attesa di verifica.</div>
            </div>
          </template>
        </template>

        <div v-if="error && !loading && !dataMismatch" class="error error-box" style="margin-top: 1em; padding: 1.5em; background: rgba(232, 53, 57, 0.15); border: 2px solid #e83539; border-radius: 4px;">
          <p style="color: #e83539; margin-bottom: 0.75em;"><strong>Errore nella verifica</strong></p>
          <p style="color: white; margin-bottom: 0;">{{ error }}</p>
        </div>
        <div v-if="success && !verifiedAlready && !dataMismatch && !loading" class="success">Verifica completata con successo!</div>
        <div v-if="dataMismatch && !loading" class="error mismatch-box" style="margin-top: 1em; padding: 1.5em; background: rgba(232, 53, 57, 0.15); border: 2px solid #e83539; border-radius: 4px;">
          <p style="color: #e83539; margin-bottom: 0.75em;"><strong>I dati del documento non corrispondono alle informazioni del tuo account.</strong></p>
          <p v-if="mismatchDetails" style="color: white; margin-bottom: 1em;">Campi non corrispondenti: 
            <span v-if="mismatchDetails.firstNameMismatch">Nome</span>
            <span v-if="mismatchDetails.firstNameMismatch && (mismatchDetails.lastNameMismatch || mismatchDetails.dateOfBirthMismatch)">, </span>
            <span v-if="mismatchDetails.lastNameMismatch">Cognome</span>
            <span v-if="mismatchDetails.lastNameMismatch && mismatchDetails.dateOfBirthMismatch">, </span>
            <span v-if="mismatchDetails.dateOfBirthMismatch">Data di nascita</span>
          </p>
          <button class="btn-custom" @click="goToEditProfile" style="margin-top: 0.5em; background-color: #e83539; color: white; border: 2px solid white; padding: 10px 20px;">
            Modifica i tuoi dati personali
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import Swal from 'sweetalert2'
import { useRoute } from 'vue-router'

const route = useRoute()
const loading = ref(false)
const error = ref(null)
const success = ref(false)
const legalGuardian = ref(false)
const attempts = ref(0)
const maxAttempts = ref(3)
const manualRequired = ref(false)
const uploading = ref(false)
const manualSuccess = ref(false)
let frontFile = null
let backFile = null
const verifiedAlready = ref(false)
const pendingIdentity = ref(false)
const dataMismatch = ref(false)
const mismatchDetails = ref(null)
let verificationStatusPollInterval = null
// constraint detail flags
const isMinor = ref(false)
const uploadedIdentityFront = ref(false)
const uploadedIdentityBack = ref(false)
const validIdentityFront = ref(false)
const validIdentityBack = ref(false)
const validLegalGuardianIdentity = ref(false)
const uploadedLegalGuardianIdentity = ref(false)

// Cambia con la tua publishable key!
const STRIPE_PK = 'pk_live_51QP1pFKXs4KZv5huj0vF3RTtJSglnwXuYgauIG0tpIlD7zHxkGxL7Xh5gPHjrfLXjtLV13WjSPzAScy2BrVppoSc00zBtu1kK1'

onMounted(() => {
  const tokenFromUrl = route.query.userToken || route.params.userToken
  if (tokenFromUrl) {
    localStorage.setItem('userToken', tokenFromUrl)
  }
  const lg = route.query.legalGuardian
  legalGuardian.value = (lg === true) || (lg === 'true') || (lg === '1') || (lg === 1)
  fetchAttempts()
  fetchConstraints()
  // NON chiamare fetchVerificationStatus() qui - mostriamo il mismatch solo dopo una verifica
})

onBeforeUnmount(() => {
  // Cleanup: ferma il polling quando il componente viene smontato
  if (verificationStatusPollInterval) {
    clearInterval(verificationStatusPollInterval)
    verificationStatusPollInterval = null
  }
})

async function fetchAttempts(){
  try {
    const res = await fetch('https://api.elitefitness.it/api/identity/attempts', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('userToken')}` }
    })
    if(!res.ok) return
    const data = await res.json()
    attempts.value = data.attempts ?? 0
    maxAttempts.value = data.maxAttempts ?? 3
    manualRequired.value = attempts.value >= maxAttempts.value
  } catch(_) {/* ignore */}
}

function showErrorModal() {
  Swal.fire({
    title: 'Qualcosa è andato storto!',
    text: 'Si è verificato un errore. Vuoi riprovare?',
    icon: 'error',
    showCancelButton: true,
    confirmButtonText: 'Riprova',
    cancelButtonText: 'Annulla',
    confirmButtonColor: '#e83539',
    cancelButtonColor: '#0c0e33',
  }).then((result) => {
    if (result.isConfirmed) {
      retryAction();
    }
  })
}

function retryAction() {
  startVerification();
}

async function startVerification() {
  loading.value = true
  error.value = null
  success.value = false
  manualSuccess.value = false
  dataMismatch.value = false
  mismatchDetails.value = null
  
  // Ferma eventuali polling precedenti
  if (verificationStatusPollInterval) {
    clearInterval(verificationStatusPollInterval)
    verificationStatusPollInterval = null
  }
  
  try {
    const url = `https://api.elitefitness.it/api/identity/create-verification-session?legalGuardian=${encodeURIComponent(legalGuardian.value ? 'true' : 'false')}`
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('userToken')}`
      }
    })
    if (!res.ok) throw new Error('Errore nella richiesta della sessione Stripe')
    const { sessionId, clientSecret } = await res.json()
    if (!sessionId) throw new Error('Session ID non ricevuto dal backend')
    if (!window.Stripe) throw new Error('Stripe.js non caricato')
    const stripe = window.Stripe(STRIPE_PK)
    const { error: stripeError } = await stripe.verifyIdentity(clientSecret)
    if (stripeError) {
      // In caso di errore Stripe, ferma il loading
      loading.value = false
      showErrorModal()
    } else {
      // NON impostare success = true qui!
      // NON fermare il loading qui - deve continuare durante il polling
      // Stripe ha completato, ma dobbiamo attendere la verifica dei dati dal backend
      // Avvia polling per controllare lo stato della verifica
      // Il loading rimane true e verrà fermato solo dal polling quando riceve un risultato finale
      startVerificationStatusPolling()
    }
  } catch (e) {
    // In caso di errore, ferma il loading
    loading.value = false
    error.value = e.message || 'Errore imprevisto'
  } finally {
    // Aggiorna i tentativi e i constraints solo se non c'è un polling attivo
    // (il polling aggiornerà i constraints quando necessario)
    if (!loading.value) {
      setTimeout(fetchAttempts, 1500)
      setTimeout(fetchConstraints, 1800)
    }
  }
}

function startVerificationStatusPolling() {
  let attempts = 0
  const maxAttempts = 30 // 30 tentativi = ~2.5 minuti (poll ogni 5 secondi)
  
  // Mostra un messaggio di attesa
  loading.value = true
  // Assicurati che success sia false all'inizio del polling
  success.value = false
  
  verificationStatusPollInterval = setInterval(async () => {
    attempts++
    
    try {
      const status = await fetchVerificationStatus()
      
      if (status && status.hasStatus) {
        loading.value = false
        
        if (status.status === 'SUCCESS') {
          // Verifica completata con successo - SOLO in questo caso impostiamo success = true
          clearInterval(verificationStatusPollInterval)
          verificationStatusPollInterval = null
          success.value = true
          dataMismatch.value = false
          mismatchDetails.value = null
          setTimeout(fetchConstraints, 1000)
        } else if (status.status === 'DATA_MISMATCH') {
          // Mismatch dati - ASSICURATI che success sia false
          clearInterval(verificationStatusPollInterval)
          verificationStatusPollInterval = null
          success.value = false
          dataMismatch.value = true
          mismatchDetails.value = status.mismatchDetails || null
        } else if (status.status === 'ERROR') {
          // Errore nella verifica
          clearInterval(verificationStatusPollInterval)
          verificationStatusPollInterval = null
          success.value = false
          dataMismatch.value = false
          error.value = 'Si è verificato un errore durante la verifica dei documenti. Riprova più tardi o contatta il supporto.'
          // Mostra un alert per rendere l'errore più visibile
          Swal.fire({
            icon: 'error',
            title: 'Errore nella verifica',
            text: error.value,
            confirmButtonColor: '#e83539',
            confirmButtonText: 'OK'
          })
        }
        return // Esci dal polling se abbiamo uno stato finale
      }
      
      // Timeout dopo maxAttempts
      if (attempts >= maxAttempts) {
        clearInterval(verificationStatusPollInterval)
        verificationStatusPollInterval = null
        loading.value = false
        success.value = false
        error.value = 'Timeout: la verifica sta richiedendo più tempo del previsto. Controlla lo stato tra qualche minuto.'
      }
    } catch (e) {
      console.error('Errore nel polling dello stato:', e)
      // Non fermare il polling su errori temporanei
    }
  }, 5000) // Poll ogni 5 secondi
}

async function fetchVerificationStatus(){
  try {
    const res = await fetch('https://api.elitefitness.it/api/identity/verification-status', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('userToken')}` }
    })
    if(!res.ok) return null
    const data = await res.json()
    
    // Restituisci i dati senza aggiornare lo stato UI qui
    // Lo stato verrà aggiornato solo nel polling dopo una verifica
    return data
  } catch(_) { 
    return null
  }
}

function goToEditProfile() {
  const token = localStorage.getItem('userToken')
  window.location.href = `/account-info?userToken=${encodeURIComponent(token)}`
}

function decodeUserIdFromToken(){
  const token = localStorage.getItem('userToken')
  if(!token) return null
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    return payload.sub || payload.user_id || payload.id || null
  } catch { return null }
}

async function uploadManual(){
  error.value = null
  manualSuccess.value = false
  uploading.value = true
  const userId = decodeUserIdFromToken()
  if(!userId){
    error.value = 'Impossibile determinare l\'utente'
    uploading.value = false
    return
  }
  try {
    await uploadSingle(frontFile, userId, legalGuardian.value ? 'IDENTITY_DOCUMENT_LEGAL_GUARDIAN_FRONT' : 'IDENTITY_DOCUMENT_FRONT')
    await uploadSingle(backFile, userId, legalGuardian.value ? 'IDENTITY_DOCUMENT_LEGAL_GUARDIAN_BACK' : 'IDENTITY_DOCUMENT_BACK')
    manualSuccess.value = true
    Swal.fire({
      title: 'Inviato',
      text: 'Documenti caricati. Riceverai una conferma dopo la validazione.',
      icon: 'success',
      confirmButtonColor: '#e83539'
    })
  setTimeout(fetchConstraints, 1500)
  } catch(e){
    error.value = e.message || 'Errore caricamento'
  } finally {
    uploading.value = false
  }
}

async function uploadSingle(file, userId, type){
  const form = new FormData()
  form.append('file', file)
  form.append('userId', userId)
  form.append('type', type)
  const res = await fetch('https://api.elitefitness.it/api/userDocuments/upload', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${localStorage.getItem('userToken')}` },
    body: form
  })
  if(!res.ok) throw new Error('Upload fallito: ' + type)
}

async function uploadMissing(){
  error.value = null
  uploading.value = true
  const userId = decodeUserIdFromToken()
  if(!userId){ error.value='Impossibile determinare l\'utente'; uploading.value=false; return }
  try {
    if(frontFile) await uploadSingle(frontFile, userId, legalGuardian.value ? 'IDENTITY_DOCUMENT_LEGAL_GUARDIAN_FRONT' : 'IDENTITY_DOCUMENT_FRONT')
    if(backFile && !legalGuardian.value) await uploadSingle(backFile, userId, legalGuardian.value ? 'IDENTITY_DOCUMENT_LEGAL_GUARDIAN_BACK' : 'IDENTITY_DOCUMENT_BACK')
    Swal.fire({ title:'Aggiornato', text:'Upload completato.', icon:'success', confirmButtonColor:'#e83539' })
    setTimeout(fetchConstraints, 1200)
  } catch(e){
    error.value = e.message || 'Errore caricamento'
  } finally { uploading.value = false }
}

async function fetchConstraints(){
  try {
    const res = await fetch('https://api.elitefitness.it/api/users/constraints', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('userToken')}` }
    })
    if(!res.ok) return
    const c = await res.json()
    isMinor.value = !!c.minor
    uploadedIdentityFront.value = !!c.uploadedIdentityFront
    uploadedIdentityBack.value = !!c.uploadedIdentityBack
    validIdentityFront.value = !!c.validIdentityFront
    validIdentityBack.value = !!c.validIdentityBack
    uploadedLegalGuardianIdentity.value = !!c.uploadedLegalGuardianIdentity
    validLegalGuardianIdentity.value = !!c.validLegalGuardianIdentity

    if(isMinor.value){
      verifiedAlready.value = validLegalGuardianIdentity.value
      pendingIdentity.value = !verifiedAlready.value && uploadedLegalGuardianIdentity.value
    } else {
      verifiedAlready.value = (validIdentityFront.value && validIdentityBack.value)
      pendingIdentity.value = !verifiedAlready.value && (uploadedIdentityFront.value || uploadedIdentityBack.value)
    }
    
    // Se i documenti sono validati, assicurati che success sia true solo se non c'è mismatch
    // Se c'è un mismatch, non mostrare success anche se i documenti risultano validati
    if (verifiedAlready.value && dataMismatch.value) {
      success.value = false
    }
  } catch(_) { /* ignore */ }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600&display=swap');

body {
    font-family: 'Montserrat', sans-serif;
    background-color: #2e348c;
}

h1,
h2,
h3,
h4 {
    font-family: 'Montserrat', sans-serif;
    font-size: 22px;
    font-weight: 600 !important;
}

@media (min-width: 1400px) {
    .document-verification-page .container, .container-lg, .container-md, .container-sm, .container-xl, .container-xxl {
        max-width: 1320px;
        width: 500px;
    }
}

.document-verification-page{
    font-family: 'Montserrat', sans-serif;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    background-color: #292D7D;
    color: white;
}

.btn-custom {
    font-family: 'Montserrat', sans-serif;
    background-color: #e83539;
    color: white;
    border: none;
    padding: 10px 20px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
    border-radius: 0;
    font-size: 1.2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.logo{
    width: 100px;
}

.loading-dots {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding-left: 5px;
}

.loading-dots span {
    display: inline-block;
    animation: bounce 1.4s infinite ease-in-out;
}

.loading-dots span:nth-child(1) {
    animation-delay: -0.32s;
}

.loading-dots span:nth-child(2) {
    animation-delay: -0.16s;
}

.loading-dots span:nth-child(3) {
    animation-delay: 0s;
}

@keyframes bounce {
    0%, 80%, 100% {
        transform: scale(0);
        opacity: 0.5;
    }
    40% {
        transform: scale(1);
        opacity: 1;
    }
}

.error { color: white; margin-top: 1em; }
.success { color: #388e3c; margin-top: 1em; }
button[disabled] { opacity: 0.6; cursor: not-allowed; }

.verification-loader {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3em 0;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top-color: #e83539;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.attempts-info { margin: 0.75em 0; font-size: 0.9rem; opacity: 0.9; }
.manual-upload-box { margin-top: 1.2em; display: flex; flex-direction: column; gap: 0.9em; }
.upload-group { text-align: left; }
.upload-group label { display:block; font-weight:600; margin-bottom:4px; }
input[type=file] { background:#fff; color:#000; padding:6px; border-radius:4px; width:100%; }
</style>
